import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Layout from 'antd/lib/layout';
import { useState } from 'react';
import { useOutletContext } from 'react-router';
import { useLocation } from 'react-router-dom';
import { DEFAULT_PAGINATION } from 'src/domain/commonConst';
import { PageFooter } from '../../../components/PageFooter';
import { PageHeader } from '../../../components/PageHeader';
import { ProgramTag } from '../../../components/ProgramTag';
import { SidebarGrid } from '../../../components/Sidebar/SidebarGrid';
import { CustomizableColumnType, CustomizableTable } from '../../../components/table/CustomizableTable';
import { CustomizableTableContextProvider } from '../../../components/table/CustomizableTableContext';
import { WithEmptyDataTable } from '../../../components/table/withEmptyDataTable';
import { useDocumentTitle } from '../../../components/useDocumentTitle';
import { EventParticipationDto } from '../../../domain/event/interface';
import { toUsefulParams } from '../../toUsefulParams';
import { usePageLocation } from '../../usePageState';
import { useEventCompanyParticipationListQuery } from 'src/domain/event/queries';
import { MemoizedDatetimePopover } from 'src/components/datetimePopover/datetimePopover';
import { exportEventsCompanyParticipation } from 'src/domain/event';
import { useEventCompanyParticipationSidebar } from '../EventCompanyParticipationSidebar/useEventCompanyParticipationSidebar';
import {
    IEventCompanyParticipationPageState,
    useEventCompanyParticipationSidebarFiltersContext,
} from '../EventCompanyParticipationSidebar/EventCompanyParticipationSidebarFiltersContext';
import { EventCompanyParticipationSidebar } from '../EventCompanyParticipationSidebar/EventCompanyParticipationSidebar';
import { EVENT_COMPANY_PARTICIPATION_PERIOD_OPTIONS } from 'src/pages/energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { compareWithLocale } from 'src/domain/compareWithLocale';
import { getColumnSortOrder } from 'src/components/table/columnFormatHelpers';
import { EventViewSwitcher } from '../components/EventViewSwitcher';

export const EventCompanyParticipationView = () => {
    const { companyId } = useOutletContext<any>();

    useDocumentTitle('Events - Company Participation View');

    const location = useLocation();
    const { notification } = App.useApp();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: IEventCompanyParticipationPageState = queryToState(location.search);

    const [exportLoading, setExportLoading] = useState(false);

    const { layoutPaddingLeft, sidebarCollapsed, setSidebarCollapsed } = useEventCompanyParticipationSidebar();

    const { filters, handleDefaultFiltersApply } = useEventCompanyParticipationSidebarFiltersContext();

    if (!pageState.filter?.period) {
        pageState.filter = pageState.filter || {};
        pageState.filter.period = EVENT_COMPANY_PARTICIPATION_PERIOD_OPTIONS[0].value;
        setPageQuery({ ...pageState });

        handleDefaultFiltersApply({
            ...filters,
            period: EVENT_COMPANY_PARTICIPATION_PERIOD_OPTIONS[0].value,
        });
    }

    const listQuery = {
        filter: {
            period: pageState.filter?.period,
            participation_statuses: pageState.filter?.participation_statuses,
            event_programs: pageState.filter?.event_programs,
            companyId,
        },
    };

    const {
        isFetching,
        isError,
        error,
        data: records,
    } = useEventCompanyParticipationListQuery(listQuery, {
        keepPreviousData: true,
        staleTime: 5 * 60 * 1000,
    });

    const meta = {
        total: records?.length || 0,
        limit: pageState.pagination?.pageSize || DEFAULT_PAGINATION.pageSize,
        offset: ((pageState.pagination?.current || 1) - 1) * (pageState.pagination?.pageSize || DEFAULT_PAGINATION.pageSize),
    };

    if (isError) {
        notification.error({
            key: 'event-company-participation-list-error',
            message: error.message || 'Cannot load events!',
        });
    }

    const columns: CustomizableColumnType<EventParticipationDto>[] = [
        {
            key: 'company.company_name',
            title: 'Company',
            dataIndex: 'company.company_name',
            width: 200,
            sorter: (a, b) => compareWithLocale(a.company.company_name, b.company.company_name),
            ...getColumnSortOrder('company.company_name', pageState.sorter),
            render: (text, record) => record.company.company_name,
        },
        {
            key: 'event_programs',
            title: 'Program',
            dataIndex: 'event_programs',
            ellipsis: { showTitle: false },
            width: 140,
            sorter: (a, b) => compareWithLocale(a.event_programs?.sort().join(','), b.event_programs?.sort().join(',')),
            ...getColumnSortOrder('event_programs', pageState.sorter),
            render: (text, record) => record.event_programs?.sort()?.map((name: string, i: number) => <ProgramTag programName={name} key={i} />),
        },
        {
            key: 'event',
            title: 'Event',
            dataIndex: 'event_datetime_start_list',
            width: 120,
            ellipsis: true,
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.event_datetime_start_list[0]?.valueOf() - b.event_datetime_start_list[0]?.valueOf(),
            ...getColumnSortOrder('event_datetime_start_list', pageState.sorter),
            render: (text, record) =>
                record.event_datetime_start_list.map((date, idx) => (
                    <MemoizedDatetimePopover timestamp={date.valueOf()} key={`memoized-datetime-${idx}`} />
                )),
        },
        {
            key: 'totalNominationKw',
            title: 'Nomination (kW)',
            dataIndex: 'totalNominationKw',
            width: 80,
            sorter: (a, b) => a.totalNominationKw - b.totalNominationKw,
            ...getColumnSortOrder('totalNominationKw', pageState.sorter),
            render: (text, record) => (record.totalNominationKw >= 0 ? record.totalNominationKw : '-'),
        },
        {
            key: 'sites',
            title: 'Sites',
            dataIndex: 'dispatchedSites',
            width: 60,
            sorter: (a, b) => a.dispatchedSites - b.dispatchedSites,
            ...getColumnSortOrder('dispatchedSites', pageState.sorter),
            render: (text, record) => record.dispatchedSites || 0,
        },
        {
            key: 'participationSummary.confirmed',
            title: 'Confirmed',
            dataIndex: 'participationSummary.confirmed',
            width: 60,
            sorter: (a, b) => a.participationSummary.confirmed - b.participationSummary.confirmed,
            ...getColumnSortOrder('participationSummary.confirmed', pageState.sorter),
            render: (text, record) => record.participationSummary.confirmed,
        },
        {
            key: 'participationSummary.partial',
            title: 'Partial',
            dataIndex: 'participationSummary.partial',
            width: 60,
            sorter: (a, b) => a.participationSummary.partial - b.participationSummary.partial,
            ...getColumnSortOrder('participationSummary.partial', pageState.sorter),
            render: (text, record) => record.participationSummary.partial,
        },
        {
            key: 'participationSummary.rejected',
            title: 'Declined',
            dataIndex: 'participationSummary.rejected',
            width: 60,
            sorter: (a, b) => a.participationSummary.rejected - b.participationSummary.rejected,
            ...getColumnSortOrder('participationSummary.rejected', pageState.sorter),
            render: (text, record) => record.participationSummary.rejected,
        },
    ];

    const exportEventsCompanyParticipationAction = async () => {
        setExportLoading(true);
        try {
            await exportEventsCompanyParticipation(listQuery);
        } catch (error: any) {
            notification.error({
                key: 'events-company-participation-export-error',
                message: error.message || 'Cannot export events company participation!',
            });
        }
        setExportLoading(false);
    };

    const onTableChange = (pagination: any, filter: any, sorter: any) => {
        const newFilter = {
            ...pageState.filter,
            ...filter,
        };

        const preparedParams = toUsefulParams({ pagination, filter: newFilter, sorter });

        setPageQuery({ ...pageState, ...preparedParams });
    };

    return (
        <>
            <CustomizableTableContextProvider configKey="eventCompanyParticipation" columns={columns}>
                <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                    <EventCompanyParticipationSidebar
                        collapsed={sidebarCollapsed}
                        onCollapseChange={setSidebarCollapsed}
                        totalRecords={meta?.total}
                        onFilterChange={() => {}}
                        gridComponent={<SidebarGrid />}
                    />

                    <Layout.Content style={{ padding: '0 26px' }}>
                        <PageHeader
                            pageTitle="Events"
                            extra={<EventViewSwitcher />}
                            actions={[
                                <Button
                                    loading={exportLoading}
                                    size="large"
                                    key="download-csv"
                                    onClick={exportEventsCompanyParticipationAction}
                                    icon={<DownloadOutlined />}
                                >
                                    Download CSV
                                </Button>,
                            ]}
                        />
                        <WithEmptyDataTable condition={isFetching} emptyDataDescription="No Data for Specified Search">
                            <CustomizableTable<EventParticipationDto>
                                size="small"
                                rowKey={makeRowKeyClosure('event-company-participation')}
                                sticky={{ offsetHeader: 62 }}
                                showHiddenColumnsCount={true}
                                dataSource={records}
                                loading={isFetching}
                                onChange={onTableChange}
                                meta={meta}
                            />
                        </WithEmptyDataTable>
                        <PageFooter />
                    </Layout.Content>
                </Layout>
            </CustomizableTableContextProvider>
        </>
    );
};

function makeRowKeyClosure(prefix: string) {
    let rowIndex = 0;
    return () => `${prefix}-${rowIndex++}`;
}
